/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Table,
  Typography,
  useTheme,
  useForm,
  Pagination,
  SearchInput,
  Icon,
  UtilitySelect,
  Option,
  DateInput,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { every as _every, mapValues as _mapValues } from 'lodash';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn, useDebounce, useSessionStorage } from 'react-use';
import { COUNTRY_OPTIONS, IDENTITY_OPTIONS } from 'utils';
import {
  SkinningUserFilters,
  SearchResponse,
  searchSkinningUsers,
} from '../api';
import { useSetSelected } from '../context';
import { AccountStatus, getLabel, VerificationStatus } from '../types';

const PAGE_SIZE = 16;

function onlyEmailAndUserIdHaveValue(filters: SkinningUserFilters): boolean {
  return (
    Boolean(filters.userId) ||
    Boolean(filters.emailAddress) ||
    _every(filters, (value, key) =>
      key === 'userId' || key === 'emailAddress' ? Boolean(value) : !value
    )
  );
}

const STATUS_OPTIONS = Object.keys(AccountStatus).map((key) => ({
  value: key,
  label: AccountStatus[key as keyof typeof AccountStatus],
}));

const VERIFICATION_STATUS_OPTIONS = Object.keys(VerificationStatus).map(
  (key) => ({
    value: key,
    label: VerificationStatus[key as keyof typeof VerificationStatus],
  })
);

export const SkinningUserList = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const setSelected = useSetSelected();

  const [filterStore, setFilterStore] = useSessionStorage<{
    filters: SkinningUserFilters;
    pagination: {
      page: number;
      size: number;
    };
  } | null>('skinningUserFilterStore');

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<SkinningUserFilters>(
    {
      loginCountry: filterStore?.filters.loginCountry || null,
      eidProviderType: filterStore?.filters.eidProviderType || null,
      externalReference: filterStore?.filters.externalReference || null,
      emailAddress: filterStore?.filters.emailAddress || null,
      name: filterStore?.filters.name || null,
      userId: filterStore?.filters.userId || null,
      personalIdNumber: filterStore?.filters.personalIdNumber || null,
      createdAtLte: filterStore?.filters.createdAtLte || null,
      createdAtGte: filterStore?.filters.createdAtGte || null,
      riskScoreGte: filterStore?.filters.riskScoreGte || null,
      status: filterStore?.filters.status || null,
      sourceOfFundsVerificationStatus:
        filterStore?.filters.sourceOfFundsVerificationStatus || null,
      emailAddressVerificationStatus:
        filterStore?.filters.emailAddressVerificationStatus || null,
    },
    {}
  );
  const headers = [
    { label: getLabel('uuid'), cellKey: 'uuid', sort: false },
    { label: getLabel('emailAddress'), cellKey: 'emailAddress', sort: false },
    { label: getLabel('country'), cellKey: 'country', sort: false },
    { label: getLabel('name'), cellKey: 'name', sort: false },
    {
      label: getLabel('externalReference'),
      cellKey: 'externalReference',
      sort: false,
    },
    { label: getLabel('createdAt'), cellKey: 'createdAt', sort: false },
    { label: getLabel('riskScore'), cellKey: 'riskScore', sort: false },
    { label: getLabel('userOrders'), cellKey: 'userOrders', sort: false },
  ];

  const [value, setSearchStore] = useSessionStorage<SearchResponse | null>(
    'skinningUserSearchResult'
  );

  const [{ loading }, search] = useAsyncFn(async () => {
    const { riskScoreGte, ...stringFilters } = fields;

    const filters: SkinningUserFilters = _mapValues(stringFilters, (v) =>
      v?.trim()
    );
    const appliedFilters = {
      filters: { riskScoreGte, ...filters },
      pagination: {
        page: onlyEmailAndUserIdHaveValue(filters) ? 1 : page,
        size: PAGE_SIZE,
      },
    };

    const data = await searchSkinningUsers(appliedFilters);
    setFilterStore(appliedFilters);
    setSearchStore(data);
    return data;
  }, [page, fields]);

  useDebounce(
    () => {
      void search();
    },
    500,
    [search]
  );

  const rows = useMemo(() => {
    if (!value) return [];

    // A workaround to reset pagination if the data rows are less than the page size
    setPage((state) => (value.aggregatedData.count <= PAGE_SIZE ? 1 : state));

    return Object.values(value.profiles).map((user) => ({
      value: {
        uuid: user.userId,
        emailAddress: user.emailAddress,
        country: COUNTRY_OPTIONS.find(
          (countryOption) => countryOption.value === user.loginCountry
        )?.label,
        name: `${user.firstName} ${user.lastName}`,
        externalReference: user.externalReference,
        createdAt: new Date(user.createdAt).toLocaleDateString('sv-se'),
        riskScore: user.kyc.riskScore,
      },
    }));
  }, [value]);

  const handleRowClicked = (row: any) => {
    if (!value) return;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-non-null-assertion
    const selected = value.profiles.find((user) => user.userId === row.uuid)!;
    setSelected(selected);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    void navigate(row.uuid);
  };

  const handleResetFilters = () => {
    setPage(1);
    setters.set({
      loginCountry: null,
      eidProviderType: null,
      externalReference: null,
      emailAddress: null,
      name: null,
      userId: null,
      personalIdNumber: null,
      createdAtLte: null,
      createdAtGte: null,
      riskScoreGte: null,
      status: null,
      sourceOfFundsVerificationStatus: null,
      emailAddressVerificationStatus: null,
    });
  };

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(1, auto)',
            alignItems: 'center',
            gap: spacing.xxs,
          }}
        >
          <Typography variant="subtitle">Skinning Users</Typography>
        </div>
      </Header>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'auto auto auto auto auto auto',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <SearchInput
          placeholder="User ID"
          value={fields.userId ?? ''}
          onChange={(e) => setters.userId?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Email"
          value={fields.emailAddress ?? ''}
          onChange={(e) => setters.emailAddress?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Name"
          value={fields.name ?? ''}
          onChange={(e) => setters.name?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Personal Number"
          value={fields.personalIdNumber ?? ''}
          onChange={(e) => setters.personalIdNumber?.set(e.target.value)}
        />
        <SearchInput
          placeholder="External Reference"
          value={fields.externalReference ?? ''}
          onChange={(e) => setters.externalReference?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Min Risk Score"
          value={fields.riskScoreGte?.toString() ?? ''}
          onChange={(e) =>
            setters.riskScoreGte?.set(
              e.target.value ? Number(e.target.value) : null
            )
          }
        />
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: '8fr 1fr',
          textWrap: 'nowrap',
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xxxs,
            flexWrap: 'wrap',
          }}
        >
          <UtilitySelect
            label={
              fields.loginCountry
                ? COUNTRY_OPTIONS.find(
                    (countryOption) =>
                      countryOption.value === fields.loginCountry
                  )?.label
                : 'Country'
            }
            listTitle="Country"
            options={COUNTRY_OPTIONS}
            value={
              fields.loginCountry
                ? COUNTRY_OPTIONS.find(
                    (countryOption) =>
                      countryOption.value === fields.loginCountry
                  ) || null
                : null
            }
            onSelect={(data) => {
              setPage(1);
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              setters.loginCountry?.set((data as Option)?.value);
            }}
          />

          <UtilitySelect
            label={
              fields.eidProviderType
                ? IDENTITY_OPTIONS.find(
                    (identityOption) =>
                      identityOption.value === fields.eidProviderType
                  )?.label
                : 'Identity'
            }
            listTitle="Identity"
            options={IDENTITY_OPTIONS}
            value={
              fields.eidProviderType
                ? IDENTITY_OPTIONS.find(
                    (identityOption) =>
                      identityOption.value === fields.eidProviderType
                  ) || null
                : null
            }
            onSelect={(data) => {
              setPage(1);
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              setters.eidProviderType?.set((data as Option)?.value);
            }}
          />

          <DateInput
            placeholder="All time"
            type="range"
            value={[
              fields.createdAtGte ? new Date(fields.createdAtGte) : null,
              fields.createdAtLte ? new Date(fields.createdAtLte) : null,
            ]}
            onChange={(e: [Date | null, Date | null]) => {
              setters.createdAtGte?.set(e[0]?.toISOString());
              setters.createdAtLte?.set(e[1]?.toISOString());
            }}
            withTime
          />

          <UtilitySelect
            label={'Status'}
            listTitle="Status"
            options={STATUS_OPTIONS}
            value={
              fields.status
                ? STATUS_OPTIONS.find(
                    (statusOption) => statusOption.value === fields.status
                  ) || null
                : null
            }
            onSelect={(data) => {
              setPage(1);
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              setters.status?.set((data as Option)?.value);
            }}
          />
          <UtilitySelect
            label={
              fields.sourceOfFundsVerificationStatus
                ? `SOF: ${fields.sourceOfFundsVerificationStatus}`
                : 'Source of Funds Status'
            }
            listTitle="Source of Funds Status"
            options={VERIFICATION_STATUS_OPTIONS}
            value={
              fields.sourceOfFundsVerificationStatus
                ? VERIFICATION_STATUS_OPTIONS.find(
                    (statusOption) =>
                      statusOption.value ===
                      fields.sourceOfFundsVerificationStatus
                  ) || null
                : null
            }
            onSelect={(data) => {
              setPage(1);
              setters.sourceOfFundsVerificationStatus?.set(
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                (data as Option)?.value
              );
            }}
          />
          <UtilitySelect
            label={
              fields.emailAddressVerificationStatus
                ? VERIFICATION_STATUS_OPTIONS.find(
                    (statusOption) =>
                      statusOption.value ===
                      fields.emailAddressVerificationStatus
                  )?.label
                : 'Email Verification Status'
            }
            listTitle="Email Verification Status"
            options={VERIFICATION_STATUS_OPTIONS}
            value={
              fields.emailAddressVerificationStatus
                ? VERIFICATION_STATUS_OPTIONS.find(
                    (statusOption) =>
                      statusOption.value ===
                      fields.emailAddressVerificationStatus
                  ) || null
                : null
            }
            onSelect={(data) => {
              setPage(1);
              setters.emailAddressVerificationStatus?.set(
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                (data as Option)?.value
              );
            }}
          />
          <UtilityButton
            variant="text"
            text="Reset filters"
            onClick={handleResetFilters}
          />
          <UtilityButton
            variant="secondary"
            text="Refresh"
            onClick={search}
            disabled={loading}
          />
          {loading && (
            <Icon
              name="spinner"
              className={{ svg: { color: palette.grey.default } }}
            />
          )}
        </div>
      </div>
      {value && (
        <>
          <div css={{ marginTop: spacing.xxs }}>
            <Table
              headers={headers}
              rows={rows}
              onRowClick={handleRowClicked}
            />
          </div>
          <div css={{ marginTop: spacing.xxs }}>
            <Pagination
              currentPage={page}
              onChange={setPage}
              numberOfElements={value.aggregatedData.count}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}
    </>
  );
};
